// JOBE-0164.3: CohortFixPreview — inline expansion that opens beneath
// a failure-class row when "Fix cohort" is clicked. v1 is preview-only —
// the "Apply to N agents" button toggles a local "applied" success
// state but does not call any backend.

import { useState } from 'react';
import { Storyboard } from '../components/Storyboard/Storyboard.jsx';
import './CohortFixPreview.css';

function titleCase(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Build the storyboard step array.
// Per-agent steps when cohort < 5 (bundle + per-agent + eval).
// Collapsed three-step storyboard when cohort >= 5 (bundle + "apply N" + eval).
export function buildStoryboardSteps(agentsAffected, proposedFix) {
  const fix = proposedFix || 'Apply the cohort patch.';
  if (agentsAffected.length >= 5) {
    return [
      { surface: 'Bundle', body: fix, doNext: `→ Apply to ${agentsAffected.length} agents` },
      { surface: 'Cohort', body: `Apply to ${agentsAffected.length} agents.`, doNext: '→ Eval' },
      { surface: 'Eval', body: 'Re-run finance-core suite across the cohort.', doNext: null },
    ];
  }
  const steps = [{ surface: 'Bundle', body: fix, doNext: agentsAffected.length > 0 ? `→ ${titleCase(agentsAffected[0])}` : '→ Eval' }];
  agentsAffected.forEach((name, i) => {
    const next = i < agentsAffected.length - 1 ? `→ ${titleCase(agentsAffected[i + 1])}` : '→ Eval';
    steps.push({
      surface: titleCase(name),
      body: `${name}@N → ${name}@N+1 (cohort patch)`,
      doNext: next,
    });
  });
  steps.push({ surface: 'Eval', body: `Re-run finance-core suite across all ${agentsAffected.length} agents.`, doNext: null });
  return steps;
}

/**
 * @param {object} props
 * @param {string} props.classId
 * @param {string} props.className — human-readable failure-class title
 * @param {string[]} props.agentsAffected
 * @param {string} [props.proposedFix]
 * @param {() => void} [props.onClose]
 */
export function CohortFixPreview({ classId, className, agentsAffected, proposedFix, onClose }) {
  const [applied, setApplied] = useState(false);
  const steps = buildStoryboardSteps(agentsAffected, proposedFix);
  const n = agentsAffected.length;

  return (
    <section
      className="cohort-fix-preview"
      data-failure-class-id={classId}
      aria-label={`Cohort fix preview for ${className}`}
    >
      <header className="cohort-fix-preview__head">
        <span className="cohort-fix-preview__title">
          COHORT FIX · {className}
        </span>
        <button
          type="button"
          className="cohort-fix-preview__close"
          onClick={onClose}
          aria-label="Close cohort-fix preview"
        >
          × CLOSE
        </button>
      </header>

      <div className="cohort-fix-preview__storyboard-wrap">
        <Storyboard steps={steps} ariaLabel={`Cohort fix storyboard for ${className}`} />
      </div>

      <div className="cohort-fix-preview__result" aria-live="polite">
        {!applied && (
          <button
            type="button"
            className="cohort-fix-preview__apply"
            onClick={() => {
              // TODO(JOBE-0164-followup): wire cohort-fix submit to POST /api/promptops/cohort-fix.
              setApplied(true);
            }}
          >
            Apply to {n} agent{n === 1 ? '' : 's'}
          </button>
        )}
        {applied && (
          <div className="cohort-fix-preview__applied-line">
            <span className="cohort-fix-preview__success">
              ✓ Cohort fix applied to {n} agent{n === 1 ? '' : 's'} — eval re-run queued.
            </span>
            <button
              type="button"
              className="cohort-fix-preview__undo"
              onClick={() => setApplied(false)}
            >
              Cohort fix applied · undo
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default CohortFixPreview;
